import { Controller } from "@hotwired/stimulus"
import { FieldCamera } from "../lib/field_camera"
import { pushForegroundArea, popForegroundArea } from "../field_foreground_time"

export default class extends Controller {
  static targets = ["sheet", "overlay", "viewfinder", "status", "flipBtn"]
  // lookup false: just close and announce the scan (the ranging-status search
  // box does its own matching), skipping the Shelf tab's [data-barcode] lookup.
  static values = { lookup: { type: Boolean, default: true } }

  connect() {
    this.sheetTarget.style.transform = "translateY(100%)"
    this.overlayTarget.style.opacity = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.overlayTarget.style.backgroundColor = "rgba(0,0,0,0.6)"
    requestAnimationFrame(() => {
      this.sheetTarget.style.transition = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
      this.overlayTarget.style.transition = "opacity 0.25s ease"
    })
    this._camera = null
    this._areaPushed = false
  }

  disconnect() {
    // Card #4838 -- a Turbo navigation away while the sheet is still open
    // (no close() in between) must not leave "scanner" stuck on the
    // foreground-time area override stack forever.
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
    this._camera?.stop()
  }

  open() {
    this.overlayTarget.style.pointerEvents = "auto"
    this.overlayTarget.style.opacity = "1"
    this.sheetTarget.style.transform = "translateY(0)"
    this._setStatus("Point camera at a barcode")
    // Card #4838 -- "in-app scanner" foreground-time area override while the
    // sheet is open, popped on close().
    pushForegroundArea("scanner")
    this._areaPushed = true
    this._camera = new FieldCamera(this.viewfinderTarget.id)
    this._camera.start((barcode) => this._onScan(barcode))
      .catch(() => this._setStatus("Camera unavailable"))
  }

  async close() {
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
    await this._camera?.stop()
    this._camera = null
    this.overlayTarget.style.opacity = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.sheetTarget.style.transform = "translateY(100%)"
  }

  async flipCamera() {
    if (!this._camera) return
    await this._camera.flip()
    this._camera.start((barcode) => this._onScan(barcode))
      .catch(() => this._setStatus("Camera unavailable"))
  }

  _onScan(barcode) {
    this.close()
    this.dispatch("scanned", { detail: { barcode } })
    if (!this.lookupValue) return
    const match = [...document.querySelectorAll("[data-barcode]")]
      .find(c => c.dataset.barcode === barcode)

    if (match) {
      match.style.display = ""
      match.scrollIntoView({ behavior: "smooth", block: "center" })
      match.classList.add("ring-2", "ring-sapphire", "ring-offset-2")
      setTimeout(() => match.classList.remove("ring-2", "ring-sapphire", "ring-offset-2"), 2500)
    } else {
      const toast = document.getElementById("barcode-no-match")
      if (toast) {
        toast.classList.remove("hidden")
        setTimeout(() => toast.classList.add("hidden"), 2500)
      }
    }
  }

  _setStatus(msg) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg
  }
}
